/**
 * ChatSidebar — controlled expandable panel for the ChatPage.
 *
 * Switches between two content variants based on participant count:
 *   - 1 participant  → ProfileVariant  (twin hero, bio, starters)
 *   - 2+ participants → GroupVariant   (cluster, settings, status)
 *
 * collapsed / onToggle are controlled by the parent (ChatPage) so the
 * parent can react to the state change (re-centering the chat column).
 * This component only renders the scrollable content panel — the toggle
 * header bar lives in ChatPage.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SidebarDivider } from '@/components/chat/SidebarPrimitives'
import { PaneSection } from '@/components/chat/PaneSection/PaneSection'
import { RoomTopics, buildRoomTopics } from '@/components/chat/RoomTopics/RoomTopics'
import { RoomMaterials, mergeRoomMaterials } from '@/components/chat/RoomMaterials/RoomMaterials'
import { cn } from '@/lib/utils'
import { TellerPicksController } from '@/hooks/useTellerPicks'

import {
  ChatRoomDto,
  ChatRoomMessageDto,
  ChatRoomParticipantDto,
  ChatRoomReactionDto,
  ChatRoomParticipantState,
  ChatRoomSettings,
  ChatRoomStatus,
} from '@digital-twins/shared-types'

import { ProfileVariant } from './ProfileVariant'
import { GroupVariant } from './GroupVariant'

export interface ChatSidebarProps {
  room: ChatRoomDto
  participants: Array<ChatRoomParticipantDto>
  participantStates?: Record<string, ChatRoomParticipantState>
  roomStatus: ChatRoomStatus
  roomTitle?: string | null
  /**
   * The viewer owns the room: only then are the title and the room settings (circles,
   * Speaking first) editable. Defaults to `room.viewerIsOwner` (false when absent, e.g.
   * shared views).
   */
  viewerIsOwner?: boolean
  /** Owner rename (useChatRoom `renameRoom`); absent keeps the title static. */
  onRename?: (title: string) => unknown
  onSettingsChange?: (partial: Partial<ChatRoomSettings>) => void
  canUpdateSettings?: boolean
  isSavingSettings?: boolean
  settingsError?: string | null
  onSend?: (text: string) => void
  /** Disables the "Ask about" starters (e.g. while a guest ask-about turn is pending). */
  startersDisabled?: boolean
  className?: string
  /** Step 11b: circles and Leads state; the pane is read-only without it. */
  tellerPicks?: TellerPicksController
  /** Step 11c: loaded messages, for Topics and the live Materials list. */
  messages?: Array<ChatRoomMessageDto>
  reactions?: Array<ChatRoomReactionDto>
  /** Keys of reactions that arrived live; only they pulse a topic. */
  liveReactionKeys?: ReadonlySet<string>
  /** A round is running (typing dots on the newest topic). */
  isRoundRunning?: boolean
  /** Readable URL of a room material key; Materials is hidden without it. */
  getMaterialUrl?: (key: string) => string
  /** Runs before a topic or material navigates (the phone sheet closes first). */
  onPaneNavigate?: () => void
  /** Loads more history when a target message is not rendered. */
  onBackfill?: () => void | Promise<void>
  /** The profile card's Ask action; absent hides it (guests, closed rooms). */
  onAskParticipant?: (participant: ChatRoomParticipantDto) => void
}

export function ChatSidebar(props: ChatSidebarProps) {
  const {
    room,
    participants,
    participantStates = {},
    roomStatus,
    roomTitle,
    viewerIsOwner = room.viewerIsOwner === true,
    onRename,
    onSettingsChange,
    canUpdateSettings = true,
    isSavingSettings = false,
    settingsError,
    onSend,
    startersDisabled = false,
    className,
    tellerPicks,
    messages = [],
    reactions = [],
    liveReactionKeys,
    isRoundRunning = false,
    getMaterialUrl,
    onPaneNavigate,
    onBackfill,
    onAskParticipant,
  } = props

  const { t } = useTranslation('app')

  const isOneOnOne = participants.length === 1

  const hasTopics = useMemo(() => buildRoomTopics(messages).length > 0, [messages])
  const hasMaterials = useMemo(
    () => Boolean(getMaterialUrl) && mergeRoomMaterials(room.materials, messages).length > 0,
    [getMaterialUrl, room.materials, messages],
  )

  const topicsSection = hasTopics ? (
    <PaneSection sectionId="topics" title={t('roomPane.topics')} dataTestId="room-topics">
      <RoomTopics
        messages={messages}
        reactions={reactions}
        liveReactionKeys={liveReactionKeys}
        isRoundRunning={isRoundRunning}
        onBeforeNavigate={onPaneNavigate}
        onBackfill={onBackfill}
      />
    </PaneSection>
  ) : null

  const materialsSection =
    hasMaterials && getMaterialUrl ? (
      <PaneSection sectionId="materials" title={t('roomPane.materials')} dataTestId="room-materials">
        <RoomMaterials
          materials={room.materials}
          messages={messages}
          getMaterialUrl={getMaterialUrl}
          onBeforeNavigate={onPaneNavigate}
          onBackfill={onBackfill}
        />
      </PaneSection>
    ) : null

  const oneOnOneSections = (
    <>
      {topicsSection && (
        <>
          <SidebarDivider />
          {topicsSection}
        </>
      )}
      {materialsSection && (
        <>
          <SidebarDivider />
          {materialsSection}
        </>
      )}
    </>
  )

  return (
    <aside
      className={cn('flex min-h-0 shrink-0 flex-col', className)}
      data-testid="chat-sidebar"
    >
      <div className="sidebar-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden rounded-2xl bg-neutral-100 p-5 dark:bg-neutral-800">
        {isOneOnOne ? (
          <ProfileVariant
            participant={participants[0]}
            populationId={room.populationId}
            populationName={room.populationName}
            roomTitle={roomTitle ?? room.title}
            viewerIsOwner={viewerIsOwner}
            onRename={onRename}
            canFetchAudience={canUpdateSettings}
            onSend={onSend}
            startersDisabled={startersDisabled}
            roomSections={oneOnOneSections}
          />
        ) : (
          <GroupVariant
            room={room}
            participants={participants}
            participantStates={participantStates}
            roomStatus={roomStatus}
            roomTitle={roomTitle}
            viewerIsOwner={viewerIsOwner}
            onRename={onRename}
            canFetchAudience={canUpdateSettings}
            onSettingsChange={onSettingsChange}
            canUpdateSettings={canUpdateSettings && viewerIsOwner && Boolean(onSettingsChange)}
            isSavingSettings={isSavingSettings}
            settingsError={settingsError}
            onSend={onSend}
            startersDisabled={startersDisabled}
            tellerPicks={tellerPicks}
            onAskParticipant={onAskParticipant}
            topicsSection={topicsSection}
            materialsSection={materialsSection}
            hasMessages={messages.length > 0}
          />
        )}
      </div>
    </aside>
  )
}
